import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './Section8.css';
import { formatCurrency } from '../utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = 'Bearer'; // Step 41 — auth via httpOnly cookie; this stub keeps existing headers harmless
const headers = { 'Authorization': API_KEY, 'Content-Type': 'application/json' };

const DISCLAIMER = "Estatel AI populates documents based on information you have provided. This is not legal advice. If you are unsure of your legal position, please consult a solicitor.";

// Authenticated PDF download — the backend pdf endpoints require the Bearer
// token, so opening them with window.open / <a href> strips auth and returns
// {"detail":"Not authenticated"}. Fetch with the header, blob the body, and
// trigger a synthetic <a download>.
async function downloadPdfWithAuth(fileUrl, suggestedName = 'document.pdf') {
  const res = await fetch(`${BACKEND_URL}${fileUrl}`, { headers: { 'Authorization': API_KEY } });
  if (!res.ok) {
    const txt = await res.text();
    window.alert(`Unable to download document (${res.status}). ${txt || ''}`.trim());
    return;
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = suggestedName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

// Stage 3 — example breach descriptions surfaced when the agent clicks the
// help icon next to the breach description box. Each entry is keyed on the
// triggering ground numbers; entries fire if any of the case's selected
// grounds match. The agent always types their own version into the box —
// these are suggestions only.
//
// Wording is the user-approved verbatim text from the Stage 3 help-text
// specification (do not paraphrase).
const BREACH_EXAMPLES = [
  {
    heading: 'Grounds 8, 10 or 11 — rent arrears',
    matches: ['8', '10', '11'],
    text:
      'The tenant has failed to pay rent and £[X] is now outstanding. ' +
      'The last payment received was on [date]. Despite reminders the ' +
      'tenant has not made any payment or contacted us to discuss the arrears.',
  },
  {
    heading: 'Ground 12 — breach of tenancy',
    matches: ['12'],
    text:
      'The tenant has breached the tenancy agreement by [describe breach — ' +
      'for example: keeping a pet without permission, subletting without ' +
      'consent, or causing damage to the property].',
  },
  {
    heading: 'Grounds 14 or 7A — anti-social behaviour',
    matches: ['14', '7A'],
    text:
      'The tenant has been causing nuisance to neighbouring properties by ' +
      '[describe behaviour — for example: playing loud music late at night, ' +
      'aggressive behaviour towards neighbours]. Complaints have been received ' +
      'on [dates].',
  },
  {
    heading: 'Ground 7B — no right to rent',
    matches: ['7B'],
    text:
      'The tenant does not have the right to rent in the United Kingdom. ' +
      'This was identified on [date] following a right to rent check.',
  },
  {
    heading: 'Grounds 1 or 1A — landlord wants to move in',
    matches: ['1', '1A'],
    text:
      'The landlord requires possession of the property as they or a ' +
      'member of their family intends to occupy it as their only or ' +
      'principal home.',
  },
  {
    heading: 'Ground 2 — landlord wants to sell',
    matches: ['2'],
    text:
      'The landlord intends to sell the property with vacant possession.',
  },
  {
    heading: 'Ground 6 — redevelopment',
    matches: ['6'],
    text:
      'The landlord intends to demolish or reconstruct the property or ' +
      'carry out substantial works that cannot reasonably be done with ' +
      'the tenant in occupation.',
  },
];

const BREACH_FALLBACK_TEXT =
  'Describe the specific breach or reason for possession in your own ' +
  'words. Include dates, amounts, and any relevant details.';

const BREACH_DISCLAIMER =
  'This is example text only. Describe the actual breach in your own ' +
  'words. Be as specific as possible — include dates, amounts, and ' +
  'specific incidents.';

function getRelevantBreachExamples(selectedGrounds) {
  const set = new Set(selectedGrounds);
  return BREACH_EXAMPLES.filter(ex => ex.matches.some(g => set.has(g)));
}

// Spec 2026-05-05 — every selected ground gets its own breach-description
// textarea pre-populated with this immutable prefix. The agent must type
// AFTER the prefix; the prefix itself cannot be deleted.
const groundPrefix = (g) => `Ground ${g} is being relied on because `;

// Returns the example object whose `matches` array contains this single
// ground, or undefined if there isn't a match.
const exampleForGround = (g) => BREACH_EXAMPLES.find(ex => ex.matches.includes(String(g)));

// Frontend mirror of GROUND_NOTICE_PERIODS in routes/section8.py.
// (months, weeks) per ground.  Months always win over weeks when present.
// All grounds default to 2 months — mirrors notice_period_for_ground().
//
// IMPORTANT: the +2 working days for first-class post is applied to the
// DEEMED SERVICE DATE in calculateEarliestProceedingsDate (below) — not
// to the period itself.  Even immediate grounds (7A / 14 / 14ZA) inherit
// the postal delay from the deemed-service step.
//
// RRA 2025 confirmed values (in force 1 May 2026):
//   Immediate (0):  7A, 14, 14ZA
//   2 weeks:        7B, 12, 13, 15
//   4 weeks:        8, 10, 11
//   4 months:       1, 1A, 4A, 6B
//   2 months:       4, 5, 6, 7, 9, 16, 17
const GROUND_PERIOD_MONTHS_WEEKS = {
  // Immediate
  '7A':   [0, 0],
  '14':   [0, 0],
  '14ZA': [0, 0],
  // 2 weeks
  '7B':   [0, 2],
  '12':   [0, 2],
  '13':   [0, 2],
  '15':   [0, 2],
  // 4 weeks
  '8':    [0, 4],
  '10':   [0, 4],
  '11':   [0, 4],
  // 4 months
  '1':    [4, 0],
  '1A':   [4, 0],
  '4A':   [4, 0],
  '6B':   [4, 0],
  // 2 months
  '4':    [2, 0],
  '5':    [2, 0],
  '6':    [2, 0],
  '7':    [2, 0],
  '9':    [2, 0],
  '16':   [2, 0],
  '17':   [2, 0],
};

// UK bank holidays — mirror of UK_BANK_HOLIDAYS in
// /app/backend/routes/section8_stages.py. Used by the deemed-service
// working-day calculation so the read-only Stage 3 "Earliest date court
// proceedings can begin" matches the backend value to the day.
const UK_BANK_HOLIDAYS = new Set([
  '2025-01-01', '2025-04-18', '2025-04-21',
  '2025-05-05', '2025-05-26', '2025-08-25',
  '2025-12-25', '2025-12-26',
  '2026-01-01', '2026-04-03', '2026-04-06',
  '2026-05-04', '2026-05-25', '2026-08-31',
  '2026-12-25', '2026-12-28',
  '2027-01-01', '2027-03-26', '2027-03-29',
  '2027-05-03', '2027-05-31', '2027-08-30',
  '2027-12-27', '2027-12-28',
]);

function isWorkingDay(d) {
  const dow = d.getDay();           // 0=Sun, 6=Sat
  if (dow === 0 || dow === 6) return false;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return !UK_BANK_HOLIDAYS.has(`${yyyy}-${mm}-${dd}`);
}

// Item 2 — Stage 3 read-only display of the earliest court-proceedings date.
// Mirrors the backend calculation in routes/section8.py exactly:
//   deemed_service = served + 2 working days  (first class post)
//                  = served                    (hand delivery / same-day)
//   longest period = longest (months, days) from GROUND_PERIOD_MONTHS_DAYS
//                    across all selected grounds; months win over days.
//   earliest = deemed_service + longest period
function calculateEarliestProceedingsDate(selectedGrounds, servedISO, method) {
  if (!selectedGrounds || selectedGrounds.length === 0) return null;
  // The backend may return notice_served_date as 'YYYY-MM-DD' (date) or
  // 'YYYY-MM-DDTHH:MM:SS' (datetime). Strip any time component and parse
  // the date portion at local midnight to avoid timezone drift.
  let base;
  if (servedISO) {
    const datePart = String(servedISO).split('T')[0];
    base = new Date(datePart + 'T00:00:00');
  } else {
    base = new Date();
  }
  base.setHours(0, 0, 0, 0);

  // Deemed service — +2 working days (excluding weekends AND UK bank
  // holidays) when serving by first class post; same day otherwise.
  let deemed = new Date(base);
  if (!method || method === 'first_class_post') {
    let added = 0;
    while (added < 2) {
      deemed.setDate(deemed.getDate() + 1);
      if (isWorkingDay(deemed)) added += 1;
    }
  }

  // Longest period (mirrors longest_notice_period).
  let months = 0;
  let weeks = 0;
  for (const g of selectedGrounds) {
    const [m, w] = GROUND_PERIOD_MONTHS_WEEKS[g] || [2, 0];
    if (m > months) months = m;
    if (w > weeks)  weeks  = w;
  }

  const earliest = new Date(deemed);
  if (months) earliest.setMonth(earliest.getMonth() + months);
  else if (weeks) earliest.setDate(earliest.getDate() + weeks * 7);

  return { deemed, earliest, methodUsed: method || 'first_class_post' };
}


// =============================================================================
// DepositStatusBanner — 2026-02 (Compliance Removals Pass 1)
// =============================================================================
// Non-blocking status indicator, shown twice in the S8 flow (grounds
// step + notice-preview step). Never gates or interrupts. Amber if
// deposit protection isn't confirmed (visible warning at both
// placements); green if confirmed (subtle at both placements — two
// green ticks would feel repetitive).
//
// Reads directly from Tenancy fields the frontend has already
// captured (piggy-backed onto the threshold-check + initiate
// responses server-side).
// =============================================================================
const DepositStatusBanner = ({ info, placement }) => {
  if (!info) return null;
  const confirmed = Boolean(
    info.deposit_scheme && info.deposit_certificate_served,
  );
  const testid = `s8-deposit-banner-${placement}`;
  if (confirmed) {
    // Green — subtle at both placements to avoid double-tick noise.
    return (
      <div
        data-testid={testid}
        data-status="confirmed"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', margin: '10px 0',
          background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 6, color: '#166534', fontSize: 13,
        }}
      >
        <span style={{ fontSize: 14 }}>✓</span>
        <span>
          Deposit protection confirmed
          {info.deposit_scheme
            ? ` (${info.deposit_scheme})`
            : ''}.
        </span>
      </div>
    );
  }
  // Amber — visible warning at both placements.
  return (
    <div
      data-testid={testid}
      data-status="not_confirmed"
      role="status"
      style={{
        padding: '12px 16px', margin: '12px 0',
        background: '#fef3c7', border: '1px solid #fbbf24',
        borderRadius: 6, color: '#78350f',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Deposit protection is not confirmed for this tenancy
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
        An unprotected deposit can be raised as a defence in
        possession proceedings. You can proceed, but consider
        confirming this first. This is not legal advice — consider
        seeking legal guidance.
      </div>
    </div>
  );
};


const Section8 = () => {
  const { tenancy_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Step 39 — Re-issue flow: when the agent clicks "Re-issue S8 Notice" on
  // an overdue Stage 3 case, Section8Case redirects here with
  // ?redraft=1&case_id=… so we can skip the threshold + compliance checklist
  // screens and land the agent directly on the grounds-selection page (the
  // case is still at Stage 3, grounds + descriptions are retained on the
  // case server-side).
  const isRedraft = searchParams.get('redraft') === '1';
  const redraftCaseId = searchParams.get('case_id');

  // Workflow step: 'route_choice' | 'threshold' | 'preconditions' | 'grounds' | 'generated' | 'instructions' | 'import' | 'import_confirm'
  // 2026-02 (Compliance Pass 2) — `preconditions` step repurposes
  // the old checklist slot to capture the two RRA statutory ticks
  // (deposit protection + Tenant Fees Act). Both routes pass
  // through it: arrears → threshold → preconditions → grounds;
  // other-grounds → preconditions → grounds. The standalone
  // deposit banners on grounds + instructions (added in Pass 1)
  // are removed — the preconditions page replaces them.
  // FIX 7 — `caseFlow` distinguishes 'arrears' (Option A — rent arrears flow,
  // shows threshold + arrears messaging) from 'other_grounds' (Option B —
  // ASB, landlord needs possession, breach of tenancy, etc.; skips arrears
  // entirely and removes rent-specific copy from the flow).
  const [step, setStep] = useState('route_choice');
  const [caseFlow, setCaseFlow] = useState(null);  // 'arrears' | 'other_grounds'
  const [loading, setLoading] = useState(true);
  const [importMode, setImportMode] = useState(false);

  // Import form state
  const [importForm, setImportForm] = useState({
    stage: 3,
    arrears_balance: '',
    notice_served_date: '',
    pcol_claim_number: '',
    pcol_issued_date: '',
    hearing_date: '',
    outcome: '',
    grounds: [],
  });
  const [importDocs, setImportDocs] = useState({ notice: null, n5: null, n119: null, rent_statement: null });
  const [importSubmitting, setImportSubmitting] = useState(false);

  // 2026-05-06 (Item 2) — Inline warning shown when the agent clicks
  // "Generate Notice" but the AgentClient correspondence address used by
  // Form 3A §5.5 is incomplete (any of address_line1 / city / postcode
  // empty). Prevents the API call until the agent populates the address
  // in Settings → Agency Details.
  const [agencyAddressMissing, setAgencyAddressMissing] = useState(false);

  // 2026-05-06 (Item 4) — Inline warning when the property's landlord is
  // a Ltd company but no director_name has been entered. Court documents
  // (Form 3A, N5 / N119 / N215 SoT) require a named director.
  const [companyDirectorMissing, setCompanyDirectorMissing] = useState(false);

  // Data
  const [thresholdData, setThresholdData] = useState(null);
  // 2026-02 (Compliance Pass 2) — Preconditions attestation state.
  // Both must be true to enable "Continue to grounds". The auto-
  // status is display-only.
  const [tenancyDepositInfo, setTenancyDepositInfo] = useState(null);
  const [depositAttested, setDepositAttested] = useState(false);
  const [tenantFeesAttested, setTenantFeesAttested] = useState(false);
  const [groundsData, setGroundsData] = useState(null);
  const [selectedGrounds, setSelectedGrounds] = useState([]);
  const [groundDescriptions, setGroundDescriptions] = useState({});
  const [breachDescription, setBreachDescription] = useState('');  // legacy single-field — kept for redraft compatibility, not displayed
  const [showBreachExamples, setShowBreachExamples] = useState(false);  // legacy bottom-of-page panel — no longer used

  // Spec 2026-05-05 — track which ground card has its example panel
  // visible. Only one open at a time so the page stays compact.
  const [openExamplePerGround, setOpenExamplePerGround] = useState(null);
  const [caseId, setCaseId] = useState(null);
  const [noticeResult, setNoticeResult] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Maintenance warning state
  const [maintenanceWarning, setMaintenanceWarning] = useState(null);
  const [maintenanceWarningDismissed, setMaintenanceWarningDismissed] = useState(false);

  // Load threshold on mount (or jump directly to grounds when this is a
  // redraft re-entry from Section8Case).
  useEffect(() => {
    if (!tenancy_id) return;
    if (isRedraft && redraftCaseId) {
      loadGroundsForRedraft(redraftCaseId);
    } else {
      // FIX 7 — Default landing is the Option A vs Option B route choice.
      // Threshold loading is deferred until the agent picks Option A.
      setLoading(false);
      setStep('route_choice');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenancy_id, isRedraft, redraftCaseId]);

  // 2026-02 (Compliance Pass 2) — Fetch tenancy deposit info before
  // rendering the preconditions page so its auto-status can display
  // without a round-trip when the agent lands. Cheap (~50 bytes).
  // 2026-02 (Compliance Pass 2 hotfix) — uses the dedicated
  // /api/section8/deposit-info endpoint (the tenancies detail route
  // does not exist here); also returns deposit_amount so the badge
  // can render a neutral "No deposit on record" state when the
  // tenancy never took a deposit.
  const fetchTenancyDepositInfo = async () => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/section8/deposit-info/${tenancy_id}`,
        { headers },
      );
      if (res.ok) {
        const t = await res.json();
        setTenancyDepositInfo({
          deposit_amount: Number(t.deposit_amount ?? 0),
          deposit_scheme: t.deposit_scheme || null,
          deposit_certificate_served: Boolean(
            t.deposit_certificate_served,
          ),
          deposit_certificate_number: t.deposit_certificate_number || null,
        });
      }
    } catch (err) {
      console.error('Deposit info fetch error:', err);
    }
  };

  // FIX 7 — Option A handler: fetch arrears threshold then route to threshold step.
  const handleChooseArrearsRoute = async () => {
    setCaseFlow('arrears');
    setStep('threshold');
    await fetchThreshold();
  };

  // Option B handler: non-arrears grounds route.
  // 2026-02 (Compliance Pass 2) — Route now goes route_choice →
  // preconditions → grounds (was: → grounds directly in Pass 1).
  const handleChooseOtherGroundsRoute = async () => {
    setCaseFlow('other_grounds');
    await fetchTenancyDepositInfo();
    setStep('preconditions');
  };

  // 2026-02 (Compliance Pass 2) — Preconditions attestation
  // handler. Called from the Continue button on the preconditions
  // page. Both ticks are required (Continue is disabled otherwise).
  // Delegates to `handleInitiateCase` which sends the ticks in the
  // POST body so the server can persist them to the Case audit
  // trail (deposit_precondition_attested / tenant_fees_attested /
  // preconditions_confirmed_by / preconditions_confirmed_at).
  const handleConfirmPreconditions = async () => {
    if (!depositAttested || !tenantFeesAttested) return;
    await handleInitiateCase();
  };

  // Step 39 — Re-issue flow: load the existing case's grounds + arrears and
  // jump straight to the grounds-selection step. No threshold/checklist
  // re-prompt because the case is already at Stage 3 with all of that work
  // already recorded.
  const loadGroundsForRedraft = async (cid) => {
    setLoading(true);
    try {
      setCaseId(cid);
      const gRes = await fetch(`${BACKEND_URL}/api/section8/grounds/${cid}`, { headers });
      if (gRes.ok) {
        const gText = await gRes.text();
        const gData = gText ? JSON.parse(gText) : {};
        setGroundsData(gData);
        // Pre-select whichever grounds the agent had on the previous notice
        // (the redraft endpoint retains case.grounds + case.ground_descriptions
        // for exactly this reason); fall back to auto-selected mandatory
        // grounds if the previous selection is empty.
        const prevSelected = Array.isArray(gData.previously_selected) && gData.previously_selected.length > 0
          ? gData.previously_selected
          : gData.grounds.filter(g => g.auto_selected).map(g => g.ground);
        setSelectedGrounds(prevSelected);
        if (gData.previously_selected_descriptions && typeof gData.previously_selected_descriptions === 'object') {
          // Ensure each redraft-loaded description still starts with the
          // immutable prefix so the new clamp logic accepts it.
          const seeded = {};
          prevSelected.forEach(g => {
            const existing = gData.previously_selected_descriptions[g];
            seeded[g] = existing && existing.trim()
              ? (existing.startsWith(groundPrefix(g)) ? existing : groundPrefix(g) + existing)
              : groundPrefix(g);
          });
          setGroundDescriptions(seeded);
        } else {
          // No previous descriptions — seed the prefix only.
          const seeded = {};
          prevSelected.forEach(g => { seeded[g] = groundPrefix(g); });
          setGroundDescriptions(seeded);
        }
        if (typeof gData.previously_selected_breach_description === 'string') {
          setBreachDescription(gData.previously_selected_breach_description);
        }
      }
      setStep('grounds');
    } catch (err) {
      console.error('Redraft grounds load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchThreshold = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/section8/threshold-check/${tenancy_id}`, { headers });
      if (res.ok) {
        const text = await res.text();
        const data = text ? JSON.parse(text) : {};
        setThresholdData(data);
        // Check for open maintenance on this property
        if (data.property_id) {
          try {
            const mRes = await fetch(`${BACKEND_URL}/api/maintenance/check-open/${data.property_id}`, { headers });
            if (mRes.ok) {
              const mText = await mRes.text();
              const mData = mText ? JSON.parse(mText) : {};
              if (mData.has_open) setMaintenanceWarning(mData);
            }
          } catch {}
        }
      }
    } catch (err) {
      console.error('Threshold check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    setProcessing(true);
    try {
      await fetch(`${BACKEND_URL}/api/section8/threshold-dismiss`, {
        method: 'POST', headers,
        body: JSON.stringify({ tenancy_id, reason: 'Agent chose to continue monitoring' })
      });
      navigate('/');
    } finally {
      setProcessing(false);
    }
  };

  // 2026-02 (Compliance Pass 2) — Arrears-path "Begin Section 8"
  // button on the threshold step now routes to the preconditions
  // page. Both routes converge there before initiation.
  const handleBeginSection8 = async () => {
    await fetchTenancyDepositInfo();
    setStep('preconditions');
  };

  const handleInitiateCase = async () => {
    setProcessing(true);
    try {
      const body = {
        tenancy_id,
        // 2026-02 (Compliance Pass 2) — Both ticks sent to the
        // server. UI gates the Continue button on both being true,
        // and the server independently enforces the same guard
        // (belt-and-braces against direct API calls).
        deposit_precondition_attested: depositAttested,
        tenant_fees_attested: tenantFeesAttested,
      };
      const res = await fetch(`${BACKEND_URL}/api/section8/initiate`, {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      const resText = await res.text();
      const resData = resText ? JSON.parse(resText) : {};
      if (res.ok) {
        setCaseId(resData.case_id);
        // 2026-02 (Compliance Removals Pass 1) — capture deposit
        // status from the initiate response so the non-blocking
        // banner can render on the grounds step. Fall back to the
        // threshold-check response (arrears path fetched it there)
        // if the initiate response somehow lacks the fields.
        setTenancyDepositInfo({
          deposit_scheme: resData.deposit_scheme
            ?? thresholdData?.deposit_scheme
            ?? null,
          deposit_certificate_served: resData.deposit_certificate_served
            ?? thresholdData?.deposit_certificate_served
            ?? false,
          deposit_certificate_number: resData.deposit_certificate_number
            ?? thresholdData?.deposit_certificate_number
            ?? null,
        });
        // Fetch grounds
        const gRes = await fetch(`${BACKEND_URL}/api/section8/grounds/${resData.case_id}`, { headers });
        if (gRes.ok) {
          const gText = await gRes.text();
          const gData = gText ? JSON.parse(gText) : {};
          setGroundsData(gData);
          // 2026-05-06 (Ground-8 auto-select fix) — auto-select rules:
          //   • Rent-arrears route (`caseFlow === 'arrears'`): retain the
          //     backend's `auto_selected` mandatory grounds (Ground 8 etc.).
          //     The agent can still deselect Ground 8 — see the soft
          //     warning rendered next to its checkbox below.
          //   • Other-grounds route (`caseFlow === 'other_grounds'`):
          //     Ground 8 must NOT be auto-selected even if the tenant
          //     happens to have ≥3 months arrears, because the agent has
          //     explicitly chosen a different route. We strip Ground 8
          //     out of the auto-selected list; an amber advisory is
          //     shown on the Ground 8 card so the agent can opt-in.
          const allAutoSelected = gData.grounds
            .filter(g => g.auto_selected).map(g => g.ground);
          const autoSelected = caseFlow === 'other_grounds'
            ? allAutoSelected.filter(g => g !== '8')
            : allAutoSelected;
          setSelectedGrounds(autoSelected);
          // Seed each auto-selected ground with its immutable prefix so
          // the textarea is ready for the agent to type.
          const seeded = {};
          autoSelected.forEach(g => { seeded[g] = groundPrefix(g); });
          setGroundDescriptions(seeded);
        }
        setStep('grounds');
      } else {
        window.alert(resData.detail || 'Failed to initiate case');
      }
    } catch (err) {
      window.alert('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const STAGE_LABELS = {
    1: 'Arrears Detected', 2: 'Threshold Crossed', 3: 'Notice Preparation',
    4: 'Notice Served', 5: 'Notice Period Active', 6: 'Start Court Proceedings',
    7: 'Hearing Preparation', 8: 'Outcome Recorded'
  };
  const ALL_GROUNDS = [
    // Mandatory
    { value: '1',  label: 'Ground 1 — Landlord or family occupation (mandatory)' },
    { value: '1A', label: 'Ground 1A — Sale of property (mandatory)' },
    { value: '4A', label: 'Ground 4A — Student HMO (mandatory)' },
    { value: '6B', label: 'Ground 6B — Enforcement compliance (mandatory)' },
    { value: '7',  label: 'Ground 7 — Death of tenant (mandatory)' },
    { value: '7A', label: 'Ground 7A — Severe ASB / criminal behaviour (mandatory)' },
    { value: '7B', label: 'Ground 7B — No right to rent (mandatory)' },
    { value: '8',  label: 'Ground 8 — Rent arrears 3+ months / 13+ weeks (mandatory)' },
    // Discretionary
    { value: '4',  label: 'Ground 4 — Educational institution let' },
    { value: '5',  label: 'Ground 5 — Minister of religion' },
    { value: '6',  label: 'Ground 6 — Demolition or reconstruction' },
    { value: '9',  label: 'Ground 9 — Suitable alternative accommodation' },
    { value: '10', label: 'Ground 10 — Some rent unpaid' },
    { value: '11', label: 'Ground 11 — Persistent delay in paying rent' },
    { value: '12', label: 'Ground 12 — Breach of tenancy' },
    { value: '13', label: 'Ground 13 — Deterioration of property' },
    { value: '14', label: 'Ground 14 — Anti-social behaviour' },
    { value: '15', label: 'Ground 15 — Deterioration of furniture' },
    { value: '16', label: 'Ground 16 — Tied accommodation (former employee)' },
    { value: '17', label: 'Ground 17 — False statement by tenant' },
  ];

  const handleImportSubmit = async () => {
    setImportSubmitting(true);
    try {
      const body = {
        tenancy_id: tenancy_id,
        stage: parseInt(importForm.stage),
        arrears_balance: parseFloat(importForm.arrears_balance) || 0,
        notice_served_date: importForm.notice_served_date || null,
        pcol_claim_number: importForm.pcol_claim_number || null,
        pcol_issued_date: importForm.pcol_issued_date || null,
        hearing_date: importForm.hearing_date || null,
        outcome: importForm.outcome || null,
        grounds: importForm.grounds,
      };
      const res = await fetch(`${BACKEND_URL}/api/section8/import-case`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (res.ok && data.success) {
        window.location.href = `/section8/case/${data.case_id}`;
      } else {
        window.alert(data.detail || 'Import failed');
      }
    } catch (err) {
      window.alert('Error: ' + err.message);
    } finally {
      setImportSubmitting(false);
    }
  };

  const toggleImportGround = (g) => {
    setImportForm(f => ({
      ...f,
      grounds: f.grounds.includes(g) ? f.grounds.filter(x => x !== g) : [...f.grounds, g],
    }));
  };


  const toggleGround = (gnum, cannotDeselect) => {
    // 2026-05-06 (Ground-8 auto-select fix) — Ground 8 is ALWAYS toggleable
    // by the agent regardless of the backend's `cannot_deselect` flag. On
    // the rent-arrears route Ground 8 starts auto-selected and the soft
    // warning rendered beside the checkbox tells the agent that deselecting
    // is unusual when arrears ≥ 3 months. The lock only applies to the
    // truly-mandatory non-arrears grounds (1, 1A, 4A, 6B, 7, 7A, 7B).
    if (cannotDeselect && gnum !== '8') return;
    setSelectedGrounds(prev =>
      prev.includes(gnum) ? prev.filter(g => g !== gnum) : [...prev, gnum]
    );
    // Spec 2026-05-05 — seed/clear the per-ground textarea alongside the
    // checkbox toggle. Selecting a ground initialises the textarea with
    // the immutable prefix; deselecting wipes the description so we don't
    // leak orphaned text into Form 3A or N119.
    setGroundDescriptions(prev => {
      const next = { ...prev };
      if (next[gnum]) {
        delete next[gnum];
      } else {
        next[gnum] = groundPrefix(gnum);
      }
      return next;
    });
  };

  // Spec 2026-05-05 — agent must type AFTER the prefix and cannot delete
  // it. Clamp the textarea value back to "<prefix><their text>".
  const updateGroundDescription = (gnum, raw) => {
    const prefix = groundPrefix(gnum);
    let body = raw;
    if (raw.startsWith(prefix)) {
      body = raw;
    } else if (prefix.startsWith(raw)) {
      // Agent has hit backspace inside the prefix — restore it fully.
      body = prefix;
    } else {
      // Agent pasted over the prefix or otherwise blew it away — re-add.
      body = prefix + raw.replace(prefix, '');
    }
    setGroundDescriptions(prev => ({ ...prev, [gnum]: body }));
  };

  // Returns true when at least one ground is selected AND every selected
  // ground has at least one character of agent text after the prefix.
  const allGroundsHaveExplanation = () => {
    if (selectedGrounds.length === 0) return false;
    return selectedGrounds.every(g => {
      const desc = (groundDescriptions[g] || '').trim();
      return desc.length > groundPrefix(g).trim().length;
    });
  };

  const handleGenerateNotice = async () => {
    if (selectedGrounds.length === 0) {
      window.alert('Please select at least one ground');
      return;
    }

    // 2026-05-06 (Item 2) — Block notice generation when the AgentClient
    // correspondence address (Form 3A §5.5) is incomplete. We fetch the
    // profile fresh on click so the agent can flip back to Settings,
    // populate the address, then come back without a stale-state hit.
    setAgencyAddressMissing(false);
    setCompanyDirectorMissing(false);
    try {
      const profileRes = await fetch(`${BACKEND_URL}/api/agency/profile`, { headers });
      const profileTxt = await profileRes.text();
      const profile = profileTxt ? JSON.parse(profileTxt) : {};
      if (profileRes.ok) {
        const al1 = (profile.address_line1 || '').trim();
        const city = (profile.city || '').trim();
        const pc = (profile.postcode || '').trim();
        if (!al1 || !city || !pc) {
          setAgencyAddressMissing(true);
          // scroll the warning into view so it's not missed
          setTimeout(() => {
            const el = document.querySelector('[data-testid="s8-agency-address-warning"]');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 50);
          return;
        }
      }
    } catch (e) {
      // Network error fetching the profile is non-fatal — fall through
      // and let the existing notice-generate API call surface its own
      // error if the address is genuinely missing on the backend side.
      console.warn('agency-profile precheck failed', e);
    }

    // 2026-05-06 (Item 4) — Block notice generation when the landlord is
    // a Ltd company but no director_name has been entered. case-detail
    // exposes landlord_type / landlord_director_name for this purpose.
    if (caseId) {
      try {
        const cdRes = await fetch(`${BACKEND_URL}/api/section8/case-detail/${caseId}`, { headers });
        if (cdRes.ok) {
          const cdTxt = await cdRes.text();
          const cd = cdTxt ? JSON.parse(cdTxt) : {};
          if ((cd.landlord_type || 'individual') === 'company'
              && !(cd.landlord_director_name || '').trim()) {
            setCompanyDirectorMissing(true);
            setTimeout(() => {
              const el = document.querySelector('[data-testid="s8-company-director-warning"]');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
            return;
          }
        }
      } catch (e) {
        console.warn('landlord precheck failed', e);
      }
    }

    setProcessing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/section8/generate-notice`, {
        method: 'POST', headers,
        body: JSON.stringify({
          case_id: caseId,
          grounds: selectedGrounds,
          // Spec 2026-05-05 — per-ground breach descriptions replace the
          // legacy single-field `breach_description`. Each value already
          // starts with "Ground X is being relied on because " (immutable
          // prefix enforced by the textarea).
          ground_descriptions: groundDescriptions,
          breach_description: '',
          // 2026-05-06 — persist Stage 3 funnel choice so Stage 6 can
          // pre-select the recommended court-filing route.
          case_flow: caseFlow || null,
        })
      });
      // Read the response body exactly once. If the call site has been
      // reached before (e.g. stale closure in a hot-reload scenario) the
      // body may already be consumed — guard against that here so the
      // user gets a useful HTTP status instead of "body stream already
      // read".
      let resText = '';
      try {
        resText = await res.text();
      } catch (readErr) {
        console.warn('generate-notice body read failed', readErr);
      }
      let resData = {};
      if (resText) {
        try {
          resData = JSON.parse(resText);
        } catch (parseErr) {
          // Non-JSON response (e.g. HTML error page from an upstream
          // proxy). Surface the raw text so the agent can diagnose.
          window.alert(`Server returned HTTP ${res.status} — non-JSON response. ` +
                       `First 200 chars: ${resText.slice(0, 200)}`);
          return;
        }
      }
      if (res.ok) {
        setNoticeResult(resData);
        setStep('instructions');
      } else {
        window.alert(resData.detail || resData.message ||
                     `Failed to generate notice (HTTP ${res.status})`);
      }
    } catch (err) {
      window.alert('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="s8-container">
        <div className="s8-loading">Loading Section 8 workflow...</div>
      </div>
    );
  }

  return (
    <div className="s8-container" data-testid="section8-page">
      <div className="s8-nav">
        <Link to="/" className="s8-back" data-testid="s8-back">Back to Dashboard</Link>
      </div>

      <div className="s8-disclaimer-banner" data-testid="s8-disclaimer">{DISCLAIMER}</div>

      {/* Import Toggle */}
      {(step === 'route_choice' || step === 'threshold' || step === 'import' || step === 'import_confirm') && (
        <div className="s8-import-toggle" data-testid="s8-import-toggle">
          <label className="s8-toggle-label">
            <input type="checkbox" checked={importMode} onChange={e => { setImportMode(e.target.checked); setStep(e.target.checked ? 'import' : 'route_choice'); }} data-testid="s8-import-checkbox" />
            <span className="s8-toggle-slider"></span>
            Is this case already in progress?
          </label>
        </div>
      )}

      {/* FIX 7 — STEP: Route Choice (Option A vs Option B).
          Replaces the old default landing on the threshold step which baked
          rent-arrears assumptions into the entire Section 8 flow. */}
      {step === 'route_choice' && !importMode && (
        <div className="s8-step" data-testid="s8-step-route-choice">
          <h1 className="s8-title">Start Section 8 — Choose grounds type</h1>
          <p className="s8-subtitle">
            Section 8 notices can be served on a wide range of grounds. Pick
            the option that best matches the reason you are seeking
            possession. You can revisit this later by exiting this flow.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '1rem', marginTop: '1.5rem' }}>
            <button type="button"
                    onClick={handleChooseArrearsRoute}
                    data-testid="s8-route-arrears"
                    style={{ textAlign: 'left', background: '#fff',
                             border: '2px solid #cbd5e1', borderRadius: 8,
                             padding: '1.1rem 1.2rem', cursor: 'pointer',
                             transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#0073EA'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#cbd5e1'}>
              <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.05rem',
                           color: '#0f172a' }}>
                Option A — Rent arrears
              </h3>
              <p style={{ margin: 0, fontSize: '0.88rem', color: '#475569' }}>
                Possession on Grounds 8, 10 and/or 11. Includes the arrears
                threshold check and arrears-specific banners and copy.
              </p>
            </button>

            <button type="button"
                    onClick={handleChooseOtherGroundsRoute}
                    data-testid="s8-route-other-grounds"
                    style={{ textAlign: 'left', background: '#fff',
                             border: '2px solid #cbd5e1', borderRadius: 8,
                             padding: '1.1rem 1.2rem', cursor: 'pointer',
                             transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#0073EA'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#cbd5e1'}>
              <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.05rem',
                           color: '#0f172a' }}>
                Option B — All grounds for possession
              </h3>
              <p style={{ margin: 0, fontSize: '0.88rem', color: '#475569' }}>
                Shows every available possession ground, including
                Grounds 8, 10 and 11. Use this for mixed cases or when
                you want full access to all grounds. Skips the arrears
                threshold check.
              </p>
            </button>
          </div>
        </div>
      )}

      {/* STEP: Import Form */}
      {step === 'import' && (
        <div className="s8-step" data-testid="s8-step-import">
          <h1 className="s8-title">Import Existing Case</h1>
          <p className="s8-subtitle">Import a Section 8 case that is already in progress. All rules will apply from the imported stage onwards.</p>

          <div className="s8-import-disclaimer" data-testid="s8-import-disclaimer">
            Estatel AI populates this case record from information you have provided. Ensure all dates and details are accurate as they will be used in any documents generated from this point forward. This is not legal advice.
          </div>

          <div className="s8-import-form" data-testid="s8-import-form">
            <div className="s8-import-field">
              <label>Current Stage *</label>
              <select value={importForm.stage} onChange={e => setImportForm(f => ({ ...f, stage: e.target.value }))} data-testid="import-stage">
                {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
              </select>
            </div>

            <div className="s8-import-field">
              <label>Arrears Balance at Import (£)</label>
              <input type="number" step="0.01" value={importForm.arrears_balance} onChange={e => setImportForm(f => ({ ...f, arrears_balance: e.target.value }))} placeholder="0.00" data-testid="import-arrears" />
            </div>

            <div className="s8-import-field">
              <label>Grounds Selected</label>
              <div className="s8-grounds-checkboxes">
                {ALL_GROUNDS.map(g => (
                  <label key={g.value} className="s8-ground-check-label">
                    <input type="checkbox" checked={importForm.grounds.includes(g.value)} onChange={() => toggleImportGround(g.value)} data-testid={`import-ground-${g.value}`} />
                    {g.label}
                  </label>
                ))}
              </div>
            </div>

            {parseInt(importForm.stage) >= 4 && (
              <div className="s8-import-field">
                <label>Notice Served Date *</label>
                <input type="date" value={importForm.notice_served_date} onChange={e => setImportForm(f => ({ ...f, notice_served_date: e.target.value }))} data-testid="import-notice-date" />
              </div>
            )}

            {parseInt(importForm.stage) >= 6 && (
              <>
                <div className="s8-import-row">
                  <div className="s8-import-field">
                    <label>PCOL Claim Number *</label>
                    <input type="text" value={importForm.pcol_claim_number} onChange={e => setImportForm(f => ({ ...f, pcol_claim_number: e.target.value }))} placeholder="e.g. E12345678" data-testid="import-pcol-number" />
                  </div>
                  <div className="s8-import-field">
                    <label>PCOL Issued Date *</label>
                    <input type="date" value={importForm.pcol_issued_date} onChange={e => setImportForm(f => ({ ...f, pcol_issued_date: e.target.value }))} data-testid="import-pcol-date" />
                  </div>
                </div>
              </>
            )}

            {parseInt(importForm.stage) >= 7 && (
              <div className="s8-import-field">
                <label>Hearing Date *</label>
                <input type="date" value={importForm.hearing_date} onChange={e => setImportForm(f => ({ ...f, hearing_date: e.target.value }))} data-testid="import-hearing-date" />
              </div>
            )}

            {parseInt(importForm.stage) === 8 && (
              <div className="s8-import-field">
                <label>Outcome *</label>
                <select value={importForm.outcome} onChange={e => setImportForm(f => ({ ...f, outcome: e.target.value }))} data-testid="import-outcome">
                  <option value="">-- Select outcome --</option>
                  <option value="possession_granted">Possession Granted</option>
                  <option value="suspended_possession">Suspended Possession Order</option>
                  <option value="dismissed">Claim Dismissed</option>
                  <option value="adjourned">Adjourned</option>
                </select>
              </div>
            )}

            {/* Document Uploads */}
            <div className="s8-import-docs">
              <h3>Upload Existing Documents (Optional)</h3>
              {parseInt(importForm.stage) >= 3 && (
                <div className="s8-import-field">
                  <label>Section 8 Notice PDF</label>
                  <input type="file" accept=".pdf" onChange={e => setImportDocs(d => ({ ...d, notice: e.target.files[0] }))} data-testid="import-doc-notice" />
                </div>
              )}
              {parseInt(importForm.stage) >= 6 && (
                <>
                  <div className="s8-import-field">
                    <label>N5 Form</label>
                    <input type="file" accept=".pdf" onChange={e => setImportDocs(d => ({ ...d, n5: e.target.files[0] }))} data-testid="import-doc-n5" />
                  </div>
                  <div className="s8-import-field">
                    <label>N119 Form</label>
                    <input type="file" accept=".pdf" onChange={e => setImportDocs(d => ({ ...d, n119: e.target.files[0] }))} data-testid="import-doc-n119" />
                  </div>
                  <div className="s8-import-field">
                    <label>PCOL Rent Statement</label>
                    <input type="file" accept=".pdf" onChange={e => setImportDocs(d => ({ ...d, rent_statement: e.target.files[0] }))} data-testid="import-doc-rent" />
                  </div>
                </>
              )}
            </div>

            <button className="btn-primary" onClick={() => setStep('import_confirm')} data-testid="btn-import-review">
              Review &amp; Confirm Import
            </button>
          </div>
        </div>
      )}

      {/* STEP: Import Confirmation */}
      {step === 'import_confirm' && (
        <div className="s8-step" data-testid="s8-step-import-confirm">
          <h1 className="s8-title">Confirm Case Import</h1>
          <p className="s8-subtitle">Review the details below carefully. This data will be used in all documents generated from this point.</p>

          <div className="s8-confirm-summary" data-testid="import-summary">
            <div className="s8-confirm-row"><span>Stage:</span><strong>{importForm.stage} — {STAGE_LABELS[importForm.stage]}</strong></div>
            <div className="s8-confirm-row"><span>Arrears Balance:</span><strong>{formatCurrency(importForm.arrears_balance || 0)}</strong></div>
            <div className="s8-confirm-row"><span>Grounds:</span><strong>{importForm.grounds.length > 0 ? importForm.grounds.map(g => `Ground ${g}`).join(', ') : 'None selected'}</strong></div>
            {importForm.notice_served_date && <div className="s8-confirm-row"><span>Notice Served:</span><strong>{importForm.notice_served_date}</strong></div>}
            {importForm.pcol_claim_number && <div className="s8-confirm-row"><span>PCOL Claim:</span><strong>{importForm.pcol_claim_number}</strong></div>}
            {importForm.pcol_issued_date && <div className="s8-confirm-row"><span>PCOL Issued:</span><strong>{importForm.pcol_issued_date}</strong></div>}
            {importForm.hearing_date && <div className="s8-confirm-row"><span>Hearing Date:</span><strong>{importForm.hearing_date}</strong></div>}
            {importForm.outcome && <div className="s8-confirm-row"><span>Outcome:</span><strong>{importForm.outcome.replace(/_/g, ' ')}</strong></div>}
            <div className="s8-confirm-row"><span>Documents:</span><strong>{Object.values(importDocs).filter(Boolean).length} file(s) attached</strong></div>
          </div>

          <div className="s8-import-disclaimer" data-testid="s8-confirm-disclaimer">
            Estatel AI populates this case record from information you have provided. Ensure all dates and details are accurate as they will be used in any documents generated from this point forward. This is not legal advice.
          </div>

          <div className="s8-confirm-actions">
            <button className="btn-secondary" onClick={() => setStep('import')} data-testid="btn-import-back">Back to Edit</button>
            <button className="btn-primary" onClick={handleImportSubmit} disabled={importSubmitting} data-testid="btn-import-confirm">
              {importSubmitting ? 'Importing...' : 'Confirm & Create Case'}
            </button>
          </div>
        </div>
      )}

      {/* STEP: Threshold Confirmation */}
      {step === 'threshold' && thresholdData && !importMode && (
        <div className="s8-step" data-testid="s8-step-threshold">
          <h1 className="s8-title">Section 8 — Threshold Assessment</h1>

          <div className={`s8-threshold-card ${thresholdData.threshold_crossed ? 's8-threshold-crossed' : ''}`}>
            <h2>{thresholdData.tenant_name}</h2>
            <p className="s8-address">{thresholdData.property_address}</p>

            <div className="s8-stat-row">
              <div className="s8-stat">
                <span className="s8-stat-label">Total Arrears</span>
                <span className="s8-stat-value s8-critical">{formatCurrency(thresholdData.total_arrears)}</span>
              </div>
              <div className="s8-stat">
                <span className="s8-stat-label">Monthly Rent</span>
                <span className="s8-stat-value">{formatCurrency(thresholdData.rent_amount)}</span>
              </div>
              <div className="s8-stat">
                <span className="s8-stat-label">Threshold ({thresholdData.threshold_months} months)</span>
                <span className="s8-stat-value">{formatCurrency(thresholdData.threshold_amount)}</span>
              </div>
              <div className="s8-stat">
                <span className="s8-stat-label">Regime</span>
                <span className="s8-stat-value s8-regime">{thresholdData.regime.replace('_', ' ')}</span>
              </div>
            </div>

            {thresholdData.threshold_crossed ? (
              <div className="s8-crossed-alert" data-testid="s8-threshold-alert">
                <strong>Section 8 threshold reached.</strong> Arrears of {formatCurrency(thresholdData.total_arrears)} exceed
                the {thresholdData.threshold_months}-month threshold of {formatCurrency(thresholdData.threshold_amount)}.
                Consider initiating Section 8 proceedings.
              </div>
            ) : (
              <div className="s8-not-crossed">
                Arrears have not yet reached the Section 8 threshold. Continue monitoring.
              </div>
            )}
          </div>

          {/* Maintenance Warning Banner */}
          {maintenanceWarning && !maintenanceWarningDismissed && (
            <div className="s8-maintenance-warning" data-testid="s8-maintenance-warning">
              <div className="s8-maint-warn-content">
                <strong>There are {maintenanceWarning.count} unresolved maintenance request{maintenanceWarning.count !== 1 ? 's' : ''} on this property.</strong>
                <p>Outstanding repairs may be raised as a defence in possession proceedings. This is not legal advice &mdash; consider seeking legal guidance before proceeding.</p>
              </div>
              <button className="s8-btn s8-btn-sm" onClick={() => setMaintenanceWarningDismissed(true)} data-testid="s8-dismiss-maint-warning">Dismiss</button>
            </div>
          )}

          <div className="s8-actions" data-testid="s8-threshold-actions">
            <button className="s8-btn s8-btn-primary" onClick={handleBeginSection8} disabled={processing} data-testid="s8-btn-begin">
              Yes &mdash; Begin Section 8
            </button>
            <button className="s8-btn s8-btn-secondary" onClick={handleDismiss} disabled={processing} data-testid="s8-btn-dismiss">
              No &mdash; Continue Monitoring
            </button>
          </div>
        </div>
      )}

      {/* STEP: Possession Preconditions Attestation
          2026-02 (Compliance Pass 2) — Repurposed from the old
          compliance-checklist slot. Sits between route selection
          and grounds; both routes converge here. Two RRA-surviving
          statutory ticks + informational deposit auto-status.
          Nothing hard-blocks the amber deposit state — the AGENT
          attestation is the actual authorising signal, and both
          checkboxes are required to enable Continue. */}
      {step === 'preconditions' && (
        <div className="s8-step" data-testid="s8-step-preconditions">
          <h1 className="s8-title">Possession Preconditions</h1>
          <p className="s8-subtitle">
            Before generating the Section 8 notice, confirm the two
            surviving statutory preconditions under the Renters&rsquo;
            Rights Act.
          </p>

          {/* ITEM 1 — Deposit protection */}
          <div
            className="s8-precondition-item"
            data-testid="s8-precondition-deposit"
            style={{
              padding: 16, margin: '16px 0',
              border: '1px solid #e5e7eb',
              borderRadius: 8, background: '#ffffff',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Deposit protection
            </div>
            {tenancyDepositInfo && (() => {
              // 2026-02 (Compliance Pass 2 hotfix) — Three-state
              // auto-status badge. Rendered as soon as
              // tenancyDepositInfo is set (fetched before the step
              // transition, so it is steady on first paint).
              const amt = Number(tenancyDepositInfo.deposit_amount || 0);
              const protectedOk = Boolean(
                tenancyDepositInfo.deposit_scheme &&
                tenancyDepositInfo.deposit_certificate_served,
              );
              if (amt <= 0) {
                // Neutral / green — no deposit was ever taken.
                return (
                  <div
                    data-testid="s8-deposit-auto-status"
                    data-status="no_deposit"
                    style={{
                      padding: '8px 12px', marginBottom: 10,
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: 6, color: '#166534',
                      fontSize: 13,
                    }}
                  >
                    ✓ No deposit on record for this tenancy
                  </div>
                );
              }
              if (protectedOk) {
                return (
                  <div
                    data-testid="s8-deposit-auto-status"
                    data-status="confirmed"
                    style={{
                      padding: '8px 12px', marginBottom: 10,
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: 6, color: '#166534',
                      fontSize: 13,
                    }}
                  >
                    ✓ Deposit protection confirmed
                    {tenancyDepositInfo.deposit_scheme
                      ? ` — ${tenancyDepositInfo.deposit_scheme}`
                      : ''}
                  </div>
                );
              }
              return (
                <div
                  data-testid="s8-deposit-auto-status"
                  data-status="not_confirmed"
                  style={{
                    padding: '10px 14px', marginBottom: 10,
                    background: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: 6, color: '#78350f',
                    fontSize: 13,
                  }}
                >
                  ⚠ Deposit protection not confirmed on record
                </div>
              );
            })()}
            <label
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                cursor: 'pointer', padding: '4px 0',
              }}
            >
              <input
                type="checkbox"
                data-testid="s8-precondition-deposit-check"
                checked={depositAttested}
                onChange={(e) => setDepositAttested(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                <strong>I confirm that either no deposit was taken
                for this tenancy, or the deposit is protected in an
                authorised scheme, or the deposit has been repaid to
                the tenant.</strong>
              </span>
            </label>
            <p style={{
              fontSize: 12, color: '#6b7280', marginTop: 8,
              marginLeft: 24, lineHeight: 1.5,
            }}>
              Under the Renters&rsquo; Rights Act, the deposit must be
              protected or repaid before possession can be sought.
            </p>
          </div>

          {/* ITEM 2 — Tenant Fees Act */}
          <div
            className="s8-precondition-item"
            data-testid="s8-precondition-tfa"
            style={{
              padding: 16, margin: '16px 0',
              border: '1px solid #e5e7eb',
              borderRadius: 8, background: '#ffffff',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Tenant Fees Act 2019
            </div>
            <label
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                cursor: 'pointer', padding: '4px 0',
              }}
            >
              <input
                type="checkbox"
                data-testid="s8-precondition-tfa-check"
                checked={tenantFeesAttested}
                onChange={(e) => setTenantFeesAttested(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                <strong>I confirm no unrepaid prohibited payment or
                unlawfully retained holding deposit applies to this
                tenancy (Tenant Fees Act 2019).</strong>
              </span>
            </label>
            <p style={{
              fontSize: 12, color: '#6b7280', marginTop: 8,
              marginLeft: 24, lineHeight: 1.5,
            }}>
              Prohibited payments include fees not on the permitted
              list — e.g. referencing, admin, inventory or cleaning
              charges. Under s.17 Tenant Fees Act 2019, an unrepaid
              prohibited payment bars possession until returned.
            </p>
          </div>

          <div className="s8-actions">
            <button
              className="s8-btn s8-btn-primary"
              onClick={handleConfirmPreconditions}
              disabled={
                !depositAttested || !tenantFeesAttested || processing
              }
              data-testid="s8-btn-preconditions-continue"
            >
              {processing ? 'Creating Case…' : 'Continue to grounds'}
            </button>
            <button
              className="s8-btn s8-btn-secondary"
              onClick={() => setStep('route_choice')}
              disabled={processing}
              data-testid="s8-btn-preconditions-back"
            >
              Back
            </button>
          </div>

          <p style={{
            marginTop: 20, fontSize: 12, color: '#6b7280',
            lineHeight: 1.5, borderTop: '1px solid #e5e7eb',
            paddingTop: 12,
          }}>
            Estatel AI populates documents from information you provide.
            This is not legal advice. If unsure of your legal position,
            consult a solicitor.
          </p>
        </div>
      )}

      {/* STEP: Grounds Selection */}
      {step === 'grounds' && groundsData && (
        <div className="s8-step" data-testid="s8-step-grounds">
          <h1 className="s8-title">Stage 3 &mdash; Notice Preparation</h1>
          <p className="s8-subtitle">Select grounds to include on the Section 8 notice</p>
          <p className="s8-regime-label">Legislation regime: <strong>{groundsData.regime === 'post_may_2026' ? 'RRA' : groundsData.regime.replace('_', ' ')}</strong></p>

          {/* 2026-02 (Compliance Pass 2) — Deposit banner removed
              from this step; the preconditions page (upstream)
              now handles deposit + Tenant Fees Act attestation. */}

          <div className="s8-supervised-banner" data-testid="s8-supervised-banner">
            All communications to this tenant are now held for approval (draft-and-approve). Full Autonomy is disabled.
          </div>

          <div className="s8-grounds-list">
            {groundsData.grounds
              // 2026-05-06 — Route-based ground filter.
              //   • Rent-arrears route → only the rent grounds
              //     (8, 10, 11) are visible.
              //   • Other-grounds route → the rent grounds (8, 10, 11)
              //     are hidden so the agent picks from the remaining
              //     non-rent grounds.
              //   • Re-issue / no route set → fall through to the full
              //     list so legacy redraft cases still render.
              .filter(g => {
                const isRentGround = g.ground === '8'
                                  || g.ground === '10'
                                  || g.ground === '11';
                if (caseFlow === 'arrears') return isRentGround;
                // 2026-05-06 — "All grounds for possession" funnel now
                // shows EVERY ground (including the rent grounds) so
                // agents with mixed or non-rent cases still have access
                // to Ground 8/10/11 when they need them.
                if (caseFlow === 'other_grounds') return true;
                return true;
              })
              .map(g => (
              <div key={g.ground} className={`s8-ground ${selectedGrounds.includes(g.ground) ? 's8-ground-selected' : ''} ${g.cannot_deselect && g.ground !== '8' ? 's8-ground-locked' : ''}`} data-testid={`s8-ground-${g.ground}`}>
                <label className="s8-ground-header">
                  <input
                    type="checkbox"
                    checked={selectedGrounds.includes(g.ground)}
                    onChange={() => toggleGround(g.ground, g.cannot_deselect)}
                    disabled={g.cannot_deselect && g.ground !== '8'}
                    data-testid={`s8-ground-check-${g.ground}`}
                  />
                  <span className="s8-ground-label">{g.label}</span>
                  {g.cannot_deselect && g.ground !== '8' && <span className="s8-ground-locked-tag">Auto-selected (mandatory)</span>}
                </label>
                <p className="s8-ground-text">{g.text}</p>

                {/* 2026-05-06 (Ground-8 auto-select fix) — soft warning
                    on the arrears route when the agent deselects Ground
                    8 even though arrears are ≥ 3 months (i.e. backend
                    flagged it `auto_selected`). Ground 8 is the safest
                    mandatory ground for arrears cases; deselecting drops
                    the case to discretionary grounds only. */}
                {g.ground === '8' && caseFlow === 'arrears'
                    && g.auto_selected && !selectedGrounds.includes('8') && (
                  <div data-testid="s8-ground-8-deselect-warning"
                       style={{ marginTop: '0.5rem',
                                padding: '0.55rem 0.75rem',
                                background: '#fffbeb',
                                border: '1px solid #fcd34d',
                                borderRadius: 4,
                                fontSize: '0.82rem',
                                color: '#92400e',
                                lineHeight: 1.45 }}>
                    <strong>Warning: </strong>
                    This tenant has 3 or more months rent arrears.
                    Ground 8 is the mandatory possession ground for
                    arrears cases — deselecting it leaves you reliant
                    only on discretionary grounds.
                  </div>
                )}

                {/* 2026-05-06 (Ground-8 auto-select fix) — amber advisory
                    on the other-grounds route when arrears are ≥ 3 months
                    (backend `auto_selected`) but Ground 8 has not been
                    opted into. The agent can choose to add it or ignore
                    it; we never auto-tick it on this route. */}
                {g.ground === '8' && caseFlow === 'other_grounds'
                    && g.auto_selected && !selectedGrounds.includes('8') && (
                  <div data-testid="s8-ground-8-advisory"
                       style={{ marginTop: '0.5rem',
                                padding: '0.55rem 0.75rem',
                                background: '#fffbeb',
                                border: '1px solid #fcd34d',
                                borderRadius: 4,
                                fontSize: '0.82rem',
                                color: '#92400e',
                                lineHeight: 1.45 }}>
                    This tenant has 3 or more months rent arrears. You
                    may wish to also rely on Ground 8.
                  </div>
                )}

                {g.notice_period_display && (
                  <div className="s8-ground-notice-period"
                       data-testid={`s8-ground-period-${g.ground}`}
                       style={{ marginTop: '0.4rem',
                                padding: '0.35rem 0.6rem',
                                background: '#f1f5f9',
                                border: '1px solid #cbd5e1',
                                borderRadius: 4,
                                fontSize: '0.8rem',
                                color: '#0f172a',
                                lineHeight: 1.45 }}>
                    <strong style={{ color: '#312e81' }}>
                      Minimum notice period:&nbsp;
                    </strong>
                    {g.notice_period_display}
                  </div>
                )}

                {/* RRA-2025 below-threshold warning surfaces on the Ground 8
                    card whenever arrears are at/above the legacy 2-month
                    marker but still below the new 3-month / 13-week
                    mandatory threshold. The agent should pivot to
                    Grounds 10 or 11 in that band. */}
                {g.below_threshold_warning && (
                  <div data-testid={`s8-ground-${g.ground}-below-threshold-warning`}
                       style={{ marginTop: '0.4rem',
                                padding: '0.55rem 0.75rem',
                                background: '#fff7ed',
                                border: '1px solid #fdba74',
                                borderRadius: 4,
                                fontSize: '0.82rem',
                                color: '#9a3412',
                                lineHeight: 1.45 }}>
                    <strong>Note:&nbsp;</strong>{g.below_threshold_warning}
                  </div>
                )}

                {g.ground === '8' || g.ground === '10' ? (
                  <div className="s8-ground-arrears">Arrears at date: {formatCurrency(groundsData.total_arrears)}</div>
                ) : null}

                {/* Spec 2026-05-05 — per-ground breach description appears
                    INSIDE the card when the ground is selected. The "?"
                    help icon sits at the top-right of the card so the
                    agent can flick through example wording for THIS
                    specific ground only. The textarea pre-fills with
                    "Ground X is being relied on because " — that prefix
                    is immutable; the agent types after it. */}
                {selectedGrounds.includes(g.ground) && (
                  <div data-testid={`s8-ground-${g.ground}-breach-block`}
                       style={{ marginTop: '0.75rem',
                                padding: '0.85rem 1rem',
                                background: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  gap: '0.5rem',
                                  marginBottom: '0.4rem' }}>
                      <label htmlFor={`s8-ground-${g.ground}-breach`}
                             style={{ flex: 1, fontWeight: 500,
                                      color: '#0f172a', fontSize: '0.9rem' }}>
                        Describe the breach for Ground {g.ground} in your own
                        words.
                      </label>
                      {/* Spec 2026-05-06 — help icon on every ground card.
                          When a ground has a curated example we surface
                          that; otherwise we fall back to the generic
                          breach-description guidance so the icon is never
                          missing. */}
                      <button type="button"
                              onClick={() => setOpenExamplePerGround(prev =>
                                prev === g.ground ? null : g.ground)}
                              data-testid={`s8-ground-${g.ground}-help-toggle`}
                              aria-label={`Show example wording for Ground ${g.ground}`}
                              title={`Show example wording for Ground ${g.ground}`}
                              style={{ flex: '0 0 auto', width: 24, height: 24,
                                       borderRadius: '50%',
                                       border: '1px solid #94a3b8',
                                       background: openExamplePerGround === g.ground ? '#0f172a' : '#fff',
                                       color: openExamplePerGround === g.ground ? '#fff' : '#0f172a',
                                       fontWeight: 600, fontSize: '0.85rem',
                                       cursor: 'pointer', lineHeight: 1,
                                       padding: 0 }}>
                        ?
                      </button>
                    </div>

                    {openExamplePerGround === g.ground && (
                      <div data-testid={`s8-ground-${g.ground}-example-panel`}
                           style={{ marginBottom: '0.55rem',
                                    padding: '0.65rem 0.8rem',
                                    background: '#fffbe6',
                                    border: '1px solid #facc15',
                                    borderRadius: 4, fontSize: '0.85rem',
                                    color: '#1f2937' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.35rem',
                                      color: '#854d0e' }}>
                          {BREACH_DISCLAIMER}
                        </div>
                        {exampleForGround(g.ground) ? (
                          <>
                            <div style={{ fontWeight: 700, marginBottom: '0.25rem',
                                          color: '#0f172a' }}>
                              {exampleForGround(g.ground).heading}
                            </div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                              “{exampleForGround(g.ground).text}”
                            </div>
                          </>
                        ) : (
                          <div data-testid={`s8-ground-${g.ground}-example-fallback`}
                               style={{ fontStyle: 'italic', color: '#475569',
                                        whiteSpace: 'pre-wrap' }}>
                            “{BREACH_FALLBACK_TEXT}”
                          </div>
                        )}
                      </div>
                    )}

                    <textarea
                      id={`s8-ground-${g.ground}-breach`}
                      value={groundDescriptions[g.ground] || groundPrefix(g.ground)}
                      onChange={e => updateGroundDescription(g.ground, e.target.value)}
                      onKeyDown={e => {
                        // Stop the agent from deleting the prefix via
                        // Backspace / Delete when the cursor sits at or
                        // before the prefix boundary.
                        const prefix = groundPrefix(g.ground);
                        const ta = e.currentTarget;
                        const start = ta.selectionStart || 0;
                        const end = ta.selectionEnd || 0;
                        if (e.key === 'Backspace' && start <= prefix.length && end <= prefix.length) {
                          e.preventDefault();
                        } else if (e.key === 'Delete' && start < prefix.length) {
                          e.preventDefault();
                        }
                      }}
                      rows={4}
                      data-testid={`s8-ground-${g.ground}-breach-textarea`}
                      style={{ width: '100%', padding: '0.6rem',
                               borderRadius: 4, border: '1px solid #cbd5e1',
                               background: '#fff', color: '#0f172a',
                               fontSize: '0.9rem', fontFamily: 'inherit',
                               resize: 'vertical' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* FIX 9 — single agent-supplied breach description that flows into
              N119 Section 4.1 reason for possession AND Form 3A Section 4.3
              landlord's explanation. Required when at least one ground is
              selected so courts have a written explanation alongside the
              statutory wording. */}
          {selectedGrounds.length > 0 && (() => {
            // Stage 3 — read-only "Earliest date court proceedings can
            // begin" mirrors the backend §2.1 calculation so the agent can
            // verify it before generating the notice.
            const earliestCalc = calculateEarliestProceedingsDate(
              selectedGrounds,
              groundsData?.notice_served_date || null,
              groundsData?.notice_service_method || null
            );
            const fmt = (d) => d ? d.toLocaleDateString('en-GB',
              { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
            const methodLabel = (m) => (
              m === 'first_class_post' ? 'First class post (+2 business days)'
              : m === 'hand_delivery'  ? 'Hand delivery (same day)'
              : m === 'process_server' ? 'Process server (same day)'
              : 'First class post (assumed — +2 business days)'
            );
            return (
              <>
                <div data-testid="s8-earliest-proceedings-block"
                     style={{ marginTop: '1rem', padding: '0.9rem 1rem',
                              border: '1px solid #c7d2fe', borderRadius: 6,
                              background: '#eef2ff' }}>
                  <div style={{ fontWeight: 600, color: '#312e81',
                                fontSize: '0.92rem',
                                marginBottom: '0.35rem' }}>
                    Earliest date court proceedings can begin
                  </div>
                  <div data-testid="s8-earliest-proceedings-value"
                       style={{ fontSize: '1.4rem', fontWeight: 700,
                                color: '#1e1b4b', letterSpacing: '0.5px' }}>
                    {earliestCalc ? fmt(earliestCalc.earliest) : '—'}
                  </div>
                  {earliestCalc && (
                    <div style={{ marginTop: '0.4rem', fontSize: '0.8rem',
                                  color: '#475569', lineHeight: 1.4 }}>
                      <div data-testid="s8-earliest-served-label">
                        Notice served:&nbsp;
                        <strong style={{ color: '#0f172a' }}>
                          {groundsData?.notice_served_date
                            ? fmt(new Date(String(groundsData.notice_served_date).split('T')[0] + 'T00:00:00'))
                            : `${fmt(new Date())} (today — not yet served)`}
                        </strong>
                      </div>
                      <div data-testid="s8-earliest-method-label">
                        Service method:&nbsp;
                        <strong style={{ color: '#0f172a' }}>
                          {methodLabel(groundsData?.notice_service_method)}
                        </strong>
                      </div>
                      <div data-testid="s8-earliest-deemed-label">
                        Deemed service date:&nbsp;
                        <strong style={{ color: '#0f172a' }}>
                          {fmt(earliestCalc.deemed)}
                        </strong>
                      </div>
                      <div style={{ marginTop: '0.25rem',
                                    fontStyle: 'italic',
                                    color: '#64748b' }}>
                        Calculated as deemed service date + longest minimum
                        notice period across selected grounds (Grounds
                        7A / 14 / 14ZA = immediate, Grounds 7B / 13 / 15
                        = 2 weeks, Grounds 8 / 10 / 11 / 5E / 5F / 5G /
                        18 = 4 weeks, Grounds 1A / 1B / 4A = 4 months,
                        all others = 2 calendar months).
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          <div className="s8-actions">
            {/* 2026-05-06 (Item 2) — Agency-address warning banner.
                Surfaces when the agent clicks Generate Notice without a
                complete AgentClient correspondence address (required by
                Form 3A §5.5). Stays visible until the agent updates the
                address in Settings → Agency Details and re-tries. */}
            {agencyAddressMissing && (
              <div
                role="alert"
                data-testid="s8-agency-address-warning"
                style={{
                  background: '#fffbeb', border: '1px solid #fcd34d',
                  color: '#92400e', borderRadius: 8,
                  padding: '0.95rem 1.1rem', marginBottom: '0.75rem',
                  fontSize: '0.92rem', lineHeight: 1.5,
                }}>
                Your agency address is not set. Form 3A requires your
                correspondence address in section 5.5. Please add it in
                Settings &gt; Agency Details before generating a Section
                8 notice.{' '}
                <a
                  href="/?view=settings&tab=agency_details"
                  data-testid="s8-link-agency-details"
                  style={{ color: '#7c2d12', fontWeight: 600,
                           textDecoration: 'underline' }}>
                  Go to Agency Details
                </a>
              </div>
            )}
            {/* 2026-05-06 (Item 4) — company-director-missing warning. */}
            {companyDirectorMissing && (
              <div
                role="alert"
                data-testid="s8-company-director-warning"
                style={{
                  background: '#fffbeb', border: '1px solid #fcd34d',
                  color: '#92400e', borderRadius: 8,
                  padding: '0.95rem 1.1rem', marginBottom: '0.75rem',
                  fontSize: '0.92rem', lineHeight: 1.5,
                }}>
                This landlord is a limited company but no director name
                has been entered. Court documents require a named
                director on the statement of truth. Please add the
                director name in Settings &gt; Landlords before
                proceeding.{' '}
                <a
                  href="/?view=settings&tab=landlords"
                  data-testid="s8-link-landlords"
                  style={{ color: '#7c2d12', fontWeight: 600,
                           textDecoration: 'underline' }}>
                  Go to Settings &gt; Landlords
                </a>
              </div>
            )}
            <button className="s8-btn s8-btn-primary"
                    onClick={handleGenerateNotice}
                    disabled={processing || !allGroundsHaveExplanation()}
                    data-testid="s8-btn-generate">
              {processing ? 'Generating...' : 'Generate Notice'}
            </button>
          </div>
        </div>
      )}

      {/* STEP: Post-Generation Instructions */}
      {step === 'instructions' && noticeResult && (
        <div className="s8-step" data-testid="s8-step-instructions">
          <h1 className="s8-title">Section 8 Notice Generated</h1>

          {/* 2026-02 (Compliance Pass 2) — Deposit banner removed
              from this step; the preconditions page (upstream)
              now handles deposit + Tenant Fees Act attestation. */}

          <div className="s8-success-card" data-testid="s8-notice-success">
            <div className="s8-success-icon">{'\u2713'}</div>
            <h2>Form 3 Notice Ready</h2>
            <p>Notice date: <strong>{noticeResult.notice_date}</strong></p>
            <p>Earliest possession proceedings: <strong>{noticeResult.notice_period_end}</strong></p>
            <p>Landlord: <strong>{noticeResult.landlord_name}</strong></p>
            <p>Tenant(s): <strong>{noticeResult.tenant_names.join(', ')}</strong></p>
            <p>Grounds: <strong>{noticeResult.grounds_selected.join(', ')}</strong></p>
            <p>Arrears at notice: <strong>{formatCurrency(noticeResult.arrears_at_notice)}</strong></p>

            <button
              type="button"
              onClick={() => downloadPdfWithAuth(noticeResult.file_url, 'section8-notice.pdf')}
              className="s8-btn s8-btn-download"
              style={{ background: '#0073EA', color: '#fff', border: 'none', cursor: 'pointer' }}
              data-testid="s8-btn-download-pdf"
            >
              Download Notice PDF
            </button>
          </div>

          <div className="s8-instructions" data-testid="s8-instructions">
            <h3>HUMAN INTERVENTION REQUIRED</h3>
            <ol>
              <li>{noticeResult.instructions.step_1}</li>
              <li>{noticeResult.instructions.step_2}
                <ul>
                  <li>{noticeResult.instructions.step_2a}</li>
                  <li>{noticeResult.instructions.step_2b}</li>
                </ul>
              </li>
              <li>{noticeResult.instructions.step_3}</li>
            </ol>
          </div>

          <div className="s8-disclaimer-banner">{DISCLAIMER}</div>

          <div className="s8-actions">
            {caseId && (
              <Link to={`/section8/case/${caseId}`} className="s8-btn s8-btn-primary" data-testid="s8-btn-go-to-case">Go to Case (Record Service)</Link>
            )}
            <Link to="/" className="s8-btn s8-btn-secondary" data-testid="s8-btn-back-dashboard">Return to Dashboard</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Section8;
